import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, Type } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';

import { NgIdleModule } from '@ng-idle/core';
import { LoggerModule, NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { RecaptchaFormsModule, RecaptchaModule, RecaptchaSettings, RECAPTCHA_SETTINGS } from 'ng-recaptcha-2';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NgxIntlTelInputModule } from '@scr/ngx-intl-tel-input';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ScrEnvironment } from 'src/environments/environment';
import { AuthorizationInterceptor } from './services/authorization-interceptor';
import { MyAccountComponent } from './components/navigation-sidebar/my-account/my-account.component';
import { AccountSummaryComponent } from './components/navigation-sidebar/my-account/account-summary/account-summary.component';
import { MyProfileComponent } from './components/navigation-sidebar/my-account/my-profile/my-profile.component';
import { UserManagementComponent } from './components/navigation-sidebar/my-account/user-management/user-management.component';
import { BillingComponent } from './components/navigation-sidebar/billing/billing.component';
import { ScrBusyInterceptor } from './services/busy.interceptor';
import { ScrServiceManager } from './services/servicemanager.service';
import { NavigationSidebarComponent } from './components/navigation-sidebar/navigation-sidebar.component';
import { PaymentMethodComponent } from './components/navigation-sidebar/my-account/payment-method/payment-method.component';
import { MessagesComponent } from './components/navigation-sidebar/messages/messages.component';
import { AddCardComponent } from './components/navigation-sidebar/my-account/payment-method/add-card/add-card.component';
import { UpdateCardComponent } from './components/navigation-sidebar/my-account/payment-method/update-card/update-card.component';
import { UpdateAchComponent } from './components/navigation-sidebar/my-account/payment-method/update-ach/update-ach.component';
import { CodeManagementComponent } from './components/navigation-sidebar/code-management/code-management.component';
import { CodeLookupComponent } from './components/navigation-sidebar/code-management/code-lookup/code-lookup.component';
import { CodeHistoryComponent } from './components/navigation-sidebar/code-management/code-history/code-history.component';
import { RegistryReportsComponent } from './components/navigation-sidebar/registry-reports/registry-reports.component';
import { OrderShortCodeComponent } from './components/navigation-sidebar/order-short-code/order-short-code.component';
import { CartComponent } from './components/navigation-sidebar/order-short-code/cart/cart.component';
import { ApplicationComponent } from './components/navigation-sidebar/order-short-code/application/application.component';
import { PaymentComponent } from './components/navigation-sidebar/order-short-code/payment/payment.component';
import { MyShortCodesComponent } from './components/navigation-sidebar/my-short-codes/my-short-codes.component';
import { ShortCodeListComponent } from './components/navigation-sidebar/my-short-codes/short-code-list/short-code-list.component';
import { NeedsActionComponent } from './components/navigation-sidebar/my-short-codes/needs-action/needs-action.component';
import { CodeTransfersComponent } from './components/navigation-sidebar/my-short-codes/code-transfers/code-transfers.component';
import { CodeTransferDialogComponent } from './components/navigation-sidebar/my-short-codes/code-transfers/transfer-dialog/code-transfer-dialog.component';
import { AcceptDeclineDialogComponent } from './components/navigation-sidebar/my-short-codes/code-transfers/accept-decline-dialog/accept-decline-dialog.component';
import { MonthlyBillingComponent } from './components/navigation-sidebar/my-short-codes/monthly-billing/monthly-billing.component';
import { IndustryReportsComponent } from './components/navigation-sidebar/industry-reports/industry-reports.component';
import { BrandIdentificationComponent } from './components/navigation-sidebar/industry-reports/brand-identification/brand-identification.component';
import { ComplaintClassificationComponent } from './components/navigation-sidebar/industry-reports/complaint-classification/complaint-classification.component';
import { InMarketMonitoringComponent } from './components/navigation-sidebar/industry-reports/in-market-monitoring/in-market-monitoring.component';
import { MonthEndReportComponent } from './components/navigation-sidebar/industry-reports/month-end-report/month-end-report.component';
import { VettingInsightsComponent } from './components/navigation-sidebar/industry-reports/vetting-insights/vetting-insights.component';
import { UserComponent } from './components/navigation-sidebar/my-account/user-management/user/user.component';
import { ReportsComponent } from './components/navigation-sidebar/reports/reports.component';
import { NeedsPaymentComponent } from './components/navigation-sidebar/billing/needs-payment/needs-payment.component';
import { ViewOrdersComponent } from './components/navigation-sidebar/billing/view-orders/view-orders.component';
import { OrderDetailComponent } from './components/navigation-sidebar/billing/order-detail/order-detail.component';
import { InvoiceComponent } from './components/navigation-sidebar/billing/invoice/invoice.component';
import { ReceiptComponent } from './components/navigation-sidebar/my-short-codes/receipt/receipt.component';
import { MonthlyStatementComponent } from './components/navigation-sidebar/my-short-codes/monthly-statement/monthly-statement.component';
import { MultiStateButtonComponent } from './components/custom-controls/multi-state-button/multi-state-button.component';
import { ApplicationThankYouComponent } from './components/navigation-sidebar/order-short-code/application-thank-you/application-thank-you.component';
import { EditDetailsComponent } from './components/navigation-sidebar/my-short-codes/edit-details/edit-details.component';
import { EditBillingDetailsComponent } from './components/navigation-sidebar/my-short-codes/edit-billing-details/edit-billing-details.component';
import { RouterService } from './services/router.service';
import { AddACHComponent } from './components/navigation-sidebar/my-account/payment-method/add-ach/add-ach.component';
import { AddPaymentComponent } from './components/navigation-sidebar/my-account/payment-method/add-payment/add-payment.component';
import { EditAccountComponent } from './components/navigation-sidebar/my-account/edit-account/edit-account.component';
import { JsonHttpInterceptor, JsonParser, PhoneCustomJsonParser } from './services/json-interceptor';
import {
  AutoRenewConfirmationComponent, ReviewComponent, BillingInformationComponent, PurchaseOrderNumberComponent, SlaAgreementComponent,
  CheckoutPaymentComponent, MonthlyBillingConfirmationComponent, PaymentInformationComponent
} from './components/navigation-sidebar/checkout';
import { ThankYouComponent } from './components/navigation-sidebar/thank-you/thank-you.component';

import {
  LinkedAccountsComponent,
  LinkedAccountsNeedsActionComponent,
  LinkedAccountsAllCodesComponent,
  LinkedAccountsListComponent,
  LinkedAccountsNavigatorComponent,
  LinkedAccountsCreateComponent
} from './components/navigation-sidebar/linked-accounts';
import { CancelShortCodeLeaseDialogComponent } from './components/navigation-sidebar/my-short-codes/code-transfers/cancel-shortcodelease/cancel-shortcodelease-dialog.component';
import { CancelShortCodeLeaseConfirmationDialogComponent } from './components/navigation-sidebar/my-short-codes/code-transfers/cancel-shortcodelease-confirmation/cancel-shortcodelease-confirmation-dialog.component';
import { SuspiciousActivityComponent } from './components/navigation-sidebar/suspicious-activity/suspicious-activity.component';
import { ReportSuspiciousActivityComponent } from './components/navigation-sidebar/suspicious-activity/report-suspicious-activity/report-suspicious-activity.component';
import { CarrierReportedActivitiesComponent } from './components/navigation-sidebar/suspicious-activity/carrier-reported-activities/carrier-reported-activities.component';

import { initializeAppFactory, sharedcomponents, validators, pipes, directives, sharedPrimeNgModules } from './app.initializer';
import { ConfigService } from './services';

import { PartnerListComponent } from './components/navigation-sidebar/partner-library/partner-list/partner-list.component';
import { PartnerLibraryComponent } from './components/navigation-sidebar/partner-library/partner-library.component';
import { AddPartnerComponent } from './components/navigation-sidebar/partner-library/add-partner/add-partner.component';
import { UpdatePartnerComponent } from './components/navigation-sidebar/partner-library/update-partner/update-partner.component';

export const loggerModuleForRoot = LoggerModule.forRoot({ level: NgxLoggerLevel.DEBUG });
export const ngIdleModuleForRoot = NgIdleModule.forRoot();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const components: Type<any>[] = [
  AcceptDeclineDialogComponent,
  AccountSummaryComponent,
  AddACHComponent,
  AddCardComponent,
  AddPaymentComponent,
  ApplicationComponent,
  ApplicationThankYouComponent,
  AutoRenewConfirmationComponent,
  BillingComponent,
  BillingInformationComponent,
  BrandIdentificationComponent,
  PartnerLibraryComponent,
  AddPartnerComponent,
  UpdatePartnerComponent,
  PartnerListComponent,
  CancelShortCodeLeaseConfirmationDialogComponent,
  CancelShortCodeLeaseDialogComponent,
  CarrierReportedActivitiesComponent,
  CartComponent,
  CheckoutPaymentComponent,
  CodeHistoryComponent,
  CodeLookupComponent,
  CodeManagementComponent,
  CodeTransferDialogComponent,
  CodeTransfersComponent,
  ComplaintClassificationComponent,
  EditAccountComponent,
  EditBillingDetailsComponent,
  EditDetailsComponent,
  InMarketMonitoringComponent,
  IndustryReportsComponent,
  InvoiceComponent,
  LinkedAccountsAllCodesComponent,
  LinkedAccountsComponent,
  LinkedAccountsCreateComponent,
  LinkedAccountsListComponent,
  LinkedAccountsNavigatorComponent,
  LinkedAccountsNeedsActionComponent,
  MessagesComponent,
  MonthEndReportComponent,
  MonthEndReportComponent,
  MonthlyBillingComponent,
  MonthlyBillingConfirmationComponent,
  MonthlyStatementComponent,
  MultiStateButtonComponent,
  MyAccountComponent,
  MyProfileComponent,
  MyShortCodesComponent,
  NavigationSidebarComponent,
  NeedsActionComponent,
  NeedsPaymentComponent,
  OrderDetailComponent,
  OrderShortCodeComponent,
  PaymentComponent,
  PaymentComponent,
  PaymentInformationComponent,
  PaymentMethodComponent,
  PurchaseOrderNumberComponent,
  ReceiptComponent,
  RegistryReportsComponent,
  ReportSuspiciousActivityComponent,
  ReportsComponent,
  ReviewComponent,
  ShortCodeListComponent,
  SlaAgreementComponent,
  SuspiciousActivityComponent,
  ThankYouComponent,
  UpdateAchComponent,
  UpdateCardComponent,
  UserComponent,
  UserManagementComponent,
  VettingInsightsComponent,
  ViewOrdersComponent
];



/** Http interceptor providers in outside-in order */
export const httpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useExisting: ScrBusyInterceptor, multi: true },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthorizationInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: JsonHttpInterceptor,
    multi: true
  }
];

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/');
}

@NgModule({
  declarations: components.concat(sharedcomponents, pipes, validators, directives),
  bootstrap: [AppComponent],
  imports: [...sharedPrimeNgModules,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    RouterModule,
    FormsModule,
    NgxIntlTelInputModule,
    NgxMaskDirective,
    NgxMaskPipe,
    AppRoutingModule,
    loggerModuleForRoot,
    ngIdleModuleForRoot,
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpClient]
    }
  }),
    BsDropdownModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    ReactiveFormsModule
  ],
  providers: [
    {
      provide: RECAPTCHA_SETTINGS,
      // pull the recaptha key from the short codes environment file.
      useFactory: (environment: ScrEnvironment) => {
        return {
          siteKey: environment.recaptchaKey
        } as RecaptchaSettings;
      },
      deps: [ScrEnvironment],
    },
    {
      provide: ScrEnvironment,
      useFactory: () => {
        return new ScrEnvironment();
      }
    },
    {
      provide: JsonParser, useClass: PhoneCustomJsonParser
    },
    DatePipe,
    MessageService,
    ConfirmationService,
    httpInterceptorProviders,
    ScrServiceManager,
    RouterService,
    provideNgxMask(),
    // new for 8.3, initializer for async stuff before load.
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [TranslateService, ScrEnvironment, ConfigService, NGXLogger],
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule { }
